import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { connect } from "react-redux";
import Modal from "@components/modal";
import { resolvePath } from "@utils/resolvePath";
import { Context } from "@components/useContext/context";
import {
  getWidgetSpecificIsVisible,
  getWidgetSpecificProps
} from "@utils/index";
import { CommonProps } from "@utils/interface";

import {
  ContentWrapper,
  TopImageWrapper,
  Title,
  SectionTitle,
  CardRow,
  Card,
  CardText,
  OrderLinks,
  OrderLink,
  DotsWrapper,
  Dot,
  PrimaryButton,
  FeatureItem,
  FeatureTitle,
  FeatureDescription,
  FeatureList
} from "./styles";

interface OrderLinkItem {
  text: string;
  url: string;
}

interface MicroSDCard {
  imagePath?: string;
  imageAlt?: string;
  title: string;
  orderText?: string;
  orderLink?: string;
  orderLinks?: OrderLinkItem[];
}

const getOrderLinks = (card: MicroSDCard): OrderLinkItem[] => {
  if (card.orderLinks?.length) {
    return card.orderLinks.filter((link) => link?.text && link?.url);
  }
  if (card.orderLink) {
    return [{ text: card.orderText || "Order", url: card.orderLink }];
  }
  return [];
};

const mapStateToProps = (state: any, ownProps: any) => ({
  is_visible: getWidgetSpecificIsVisible(
    state.configuration?.normalisedConfig,
    ownProps.id
  ),
  ...getWidgetSpecificProps(state.configuration?.normalisedConfig, ownProps.id)
});

interface features {
  title: string;
  description: string;
}
interface MicroSDModalProps extends CommonProps {
  title?: string;
  topImageSrc?: string;
  topImageAlt?: string;
  howToChooseTitle?: string;
  requiredFeatures?: features[];
  recommendedCardsTitle?: string;
  recommendedCards?: MicroSDCard[];
  buttonText?: string;
}

const MicroSDModal = (props: MicroSDModalProps) => {
  const {
    is_visible,
    actions,
    title,
    topImageSrc,
    topImageAlt,
    howToChooseTitle,
    requiredFeatures,
    recommendedCardsTitle,
    recommendedCards,
    buttonText
  } = props;
  const { isDesktop } = useContext(Context);
  const { on_close_modal, on_click } = actions || {};
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const cardRowRef = useRef<HTMLDivElement>(null);
  const dragDistanceRef = useRef(0);
  const cardCount = recommendedCards?.length || 0;
  const cardsSignature = useMemo(
    () => (recommendedCards || []).map((card) => card.title).join("|"),
    [recommendedCards]
  );

  const handleClose = () => {
    on_close_modal?.forEach((eachFunc: any) => {
      typeof eachFunc.funCall === "function" && eachFunc.funCall(eachFunc.args);
    });
  };

  const handleButtonClick = () => {
    on_click?.forEach((eachFunc: any) => {
      typeof eachFunc.funCall === "function" && eachFunc.funCall(eachFunc.args);
    });
  };

  const syncActiveIndex = useCallback(() => {
    const row = cardRowRef.current;
    if (!row) return;
    const cards = Array.from(row.children) as HTMLElement[];
    if (!cards.length) return;

    const rowLeft = row.getBoundingClientRect().left;
    let nearest = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - rowLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });

    setActiveCardIndex((current) => (current === nearest ? current : nearest));
  }, []);

  useEffect(() => {
    setActiveCardIndex(0);
    cardRowRef.current?.scrollTo({ left: 0 });
  }, [cardsSignature]);

  const scrollToCard = (index: number) => {
    const row = cardRowRef.current;
    const card = row?.children?.[index] as HTMLElement | undefined;
    if (!row || !card) return;
    row.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
    setActiveCardIndex(index);
  };

  useEffect(() => {
    const cardRow = cardRowRef.current;
    if (!is_visible || !cardRow || cardCount === 0) return;

    let isDragging = false;
    let startX = 0;
    let startScroll = 0;
    dragDistanceRef.current = 0;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      isDragging = true;
      dragDistanceRef.current = 0;
      startX = event.clientX;
      startScroll = cardRow.scrollLeft;
      cardRow.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!isDragging) return;
      const delta = event.clientX - startX;
      dragDistanceRef.current = Math.abs(delta);
      cardRow.scrollLeft = startScroll - delta;
      syncActiveIndex();
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!isDragging) return;
      isDragging = false;
      if (cardRow.hasPointerCapture(event.pointerId)) {
        cardRow.releasePointerCapture(event.pointerId);
      }
      const cards = Array.from(cardRow.children) as HTMLElement[];
      const rowLeft = cardRow.getBoundingClientRect().left;
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - rowLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });
      cards[nearest] &&
        cardRow.scrollTo({
          left: cards[nearest].offsetLeft,
          behavior: "smooth"
        });
      setActiveCardIndex(nearest);
    };

    const onClickCapture = (event: Event) => {
      if (dragDistanceRef.current > 5) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const cards = Array.from(cardRow.children);
    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              const mostVisible = entries.reduce((best, entry) =>
                entry.intersectionRatio > best.intersectionRatio ? entry : best
              );
              if (!mostVisible?.isIntersecting) return;
              const index = cards.indexOf(mostVisible.target);
              if (index >= 0) {
                setActiveCardIndex((current) =>
                  current === index ? current : index
                );
              }
            },
            { root: cardRow, threshold: [0.35, 0.5, 0.7, 0.9] }
          )
        : null;
    cards.forEach((card) => observer?.observe(card));

    cardRow.addEventListener("pointerdown", onPointerDown);
    cardRow.addEventListener("pointermove", onPointerMove);
    cardRow.addEventListener("pointerup", onPointerUp);
    cardRow.addEventListener("pointercancel", onPointerUp);
    cardRow.addEventListener("click", onClickCapture, true);
    syncActiveIndex();

    return () => {
      observer?.disconnect();
      cardRow.removeEventListener("pointerdown", onPointerDown);
      cardRow.removeEventListener("pointermove", onPointerMove);
      cardRow.removeEventListener("pointerup", onPointerUp);
      cardRow.removeEventListener("pointercancel", onPointerUp);
      cardRow.removeEventListener("click", onClickCapture, true);
    };
  }, [is_visible, cardCount, cardsSignature, syncActiveIndex]);

  return (
    <Modal
      showModal={is_visible}
      onClose={handleClose}
      showModalFooter={false}
      hideCloseButton={true}
      actions={actions}
      wrapperStyles={{ padding: "0" }}
      styles={{ padding: "0", overflowX: "hidden", overflowY: "auto" }}
      lockBackgroundInteraction={props.lockBackgroundInteraction}
    >
      {topImageSrc && (
        <TopImageWrapper>
          <img
            src={resolvePath(topImageSrc || "")}
            alt={topImageAlt || ""}
            width={504}
            height={280}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
              display: "block"
            }}
          />
        </TopImageWrapper>
      )}

      <ContentWrapper isDesktop={isDesktop}>
        {title && <Title>{title}</Title>}

        {howToChooseTitle && <SectionTitle>{howToChooseTitle}</SectionTitle>}
        <FeatureList>
          {requiredFeatures &&
            requiredFeatures.map((feature, index) => (
              <FeatureItem key={index}>
                <FeatureTitle>{feature.title}: </FeatureTitle>
                <FeatureDescription> {feature.description}</FeatureDescription>
              </FeatureItem>
            ))}
        </FeatureList>
        {recommendedCardsTitle && (
          <SectionTitle>{recommendedCardsTitle}</SectionTitle>
        )}

        {recommendedCards && recommendedCards.length > 0 && (
          <>
            <CardRow
              ref={cardRowRef}
              onScroll={syncActiveIndex}
              onTouchMove={syncActiveIndex}
              onTouchEnd={syncActiveIndex}
            >
              {recommendedCards.map((card, index) => {
                const orderLinks = getOrderLinks(card);
                return (
                  <Card key={`${card.title}-${index}`}>
                    <img
                      src={resolvePath(card.imagePath || "")}
                      alt={card.imageAlt}
                      width={48}
                      height={32}
                    />
                    <CardText>
                      {card.title}
                      {orderLinks.length > 0 && (
                        <OrderLinks>
                          {orderLinks.map((link) => (
                            <OrderLink
                              key={`${link.text}-${link.url}`}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {link.text}
                            </OrderLink>
                          ))}
                        </OrderLinks>
                      )}
                    </CardText>
                  </Card>
                );
              })}
            </CardRow>

            <DotsWrapper>
              {recommendedCards.map((_, index) => (
                <Dot
                  key={index}
                  active={index === activeCardIndex}
                  onClick={() => scrollToCard(index)}
                  role="button"
                  aria-label={`Show card ${index + 1}`}
                />
              ))}
            </DotsWrapper>
          </>
        )}

        {buttonText && (
          <PrimaryButton onClick={handleButtonClick}>{buttonText}</PrimaryButton>
        )}
      </ContentWrapper>
    </Modal>
  );
};

export default connect(mapStateToProps)(MicroSDModal);
